import { useCallback, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Loader2, RotateCcw, Search, Users } from 'lucide-react'
import { Badge } from '@quebon/ui/components/badge'
import { Button } from '@quebon/ui/components/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@quebon/ui/components/dialog'
import { Field, FieldGroup, FieldLabel } from '@quebon/ui/components/field'
import { Input } from '@quebon/ui/components/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@quebon/ui/components/table'
import { Textarea } from '@quebon/ui/components/textarea'
import { handleApiError } from '@/api/client'
import {
  bulkChangeStage,
  fetchLeadDetail,
  fetchLeads,
  type LeadFilter,
  type LeadListItem,
} from '@/api/lcCrm'
import {
  ATTENDANCE_STATUS_LABEL,
  LEAD_PRIORITY_LABEL,
  LEAD_PRIORITY_ORDER,
  LEAD_STAGE_LABEL,
  LEAD_STAGE_ORDER,
  daysSinceKst,
  formatKstDate,
  labelOf,
  formatPhone,
  staleLevel,
} from './crmLabels'
import {
  EmptyRow,
  ErrorBlock,
  FilterSelect,
  LabeledSelect,
  LoadingBlock,
  Pagination,
  PriorityBadge,
  StageBadge,
  StaleDaysCell,
} from './crmComponents'

const PAGE_SIZE = 20

const STALE_FILTERS = [
  { value: 'warning', label: '31일 이상' },
  { value: 'danger', label: '61일 이상' },
]

/**
 * FR-21·22 리드 목록.
 *
 * 단계·이름/전화번호 검색·담당자는 서버 filter다 (검색과 담당자는 2026-09-07 QA로 추가).
 * 우선순위·체류일은 현재 페이지 안에서 좁힌다. 그래서 그 둘을 걸면 "이 페이지에서 N건"이라고
 * 밝힌다 — 전체에서 N건으로 읽히면 곤란하다.
 */
export function CrmLeadsPage() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [filter, setFilter] = useState<LeadFilter>({ page: 0, size: PAGE_SIZE })
  const [searchInput, setSearchInput] = useState('')
  const [teamInput, setTeamInput] = useState('')
  const [priority, setPriority] = useState<string | undefined>()
  const [stale, setStale] = useState<string | undefined>()
  const [selected, setSelected] = useState<Record<number, number>>({})
  const [bulkOpen, setBulkOpen] = useState(false)

  const { data, isLoading, error } = useQuery({
    queryKey: ['lc-crm-leads', filter],
    queryFn: () => fetchLeads(filter),
  })

  const applyTextFilters = useCallback(() => {
    setFilter((prev) => ({
      ...prev,
      search: searchInput.trim() || undefined,
      assignedTeam: teamInput.trim() || undefined,
      page: 0,
    }))
    setSelected({})
  }, [searchInput, teamInput])

  const rows = useMemo(() => {
    const all = data?.content ?? []
    return all.filter((lead) => {
      if (priority && lead.priority !== priority) return false
      if (stale) {
        const level = staleLevel(daysSinceKst(lead.lastActivityAt))
        if (stale === 'danger' && level !== 'danger') return false
        if (stale === 'warning' && level === 'normal') return false
      }
      return true
    })
  }, [data, priority, stale])

  const narrowed = Boolean(priority || stale)

  const selectedIds = Object.keys(selected).map(Number)

  const toggle = useCallback((lead: LeadListItem, checked: boolean) => {
    setSelected((prev) => {
      const next = { ...prev }
      if (checked) next[lead.leadId] = 0
      else delete next[lead.leadId]
      return next
    })
  }, [])

  const toggleAll = useCallback(
    (checked: boolean) => {
      if (!checked) {
        setSelected({})
        return
      }
      const next: Record<number, number> = {}
      for (const lead of rows) next[lead.leadId] = 0
      setSelected(next)
    },
    [rows],
  )

  const resetFilter = () => {
    setFilter({ page: 0, size: PAGE_SIZE })
    setSearchInput('')
    setTeamInput('')
    setPriority(undefined)
    setStale(undefined)
    setSelected({})
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">리드</h2>
          <p className="text-sm text-muted-foreground">
            계약 전 파이프라인을 단계·우선순위·체류일로 봅니다
          </p>
        </div>
        <Button disabled={selectedIds.length === 0} onClick={() => setBulkOpen(true)}>
          선택 {selectedIds.length}건 단계 변경
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-muted/40 p-3">
        <Field className="min-w-56 flex-1">
          <FieldLabel htmlFor="lead-search">검색</FieldLabel>
          <div className="relative">
            <Search className="absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="lead-search"
              className="pl-8"
              placeholder="이름, 전화번호로 검색"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applyTextFilters()}
            />
          </div>
        </Field>
        <FilterSelect
          className="w-44"
          label="단계"
          value={filter.stage}
          onChange={(value) => {
            setFilter((prev) => ({ ...prev, stage: value, page: 0 }))
            setSelected({})
          }}
          items={[...LEAD_STAGE_ORDER, 'HOLD_CLOSED'].map((value) => ({
            value,
            label: LEAD_STAGE_LABEL[value],
          }))}
        />
        <FilterSelect
          className="w-40"
          label="우선순위"
          value={priority}
          onChange={setPriority}
          items={LEAD_PRIORITY_ORDER.map((value) => ({ value, label: LEAD_PRIORITY_LABEL[value] }))}
        />
        <FilterSelect
          className="w-36"
          label="체류일"
          value={stale}
          onChange={setStale}
          items={STALE_FILTERS}
        />
        <Field className="w-40">
          <FieldLabel htmlFor="lead-team">담당자</FieldLabel>
          <Input
            id="lead-team"
            placeholder="팀 이름"
            value={teamInput}
            onChange={(e) => setTeamInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applyTextFilters()}
          />
        </Field>
        <Button variant="outline" className="mb-0.5" onClick={applyTextFilters}>
          <Search className="size-4" />
          검색
        </Button>
        <Button variant="ghost" className="mb-0.5" onClick={resetFilter}>
          <RotateCcw className="size-4" />
          초기화
        </Button>
      </div>

      {narrowed && (
        <p className="text-sm text-muted-foreground">
          우선순위·체류일은 현재 페이지 안에서만 좁힙니다. 이 페이지 {rows.length}건.
        </p>
      )}

      {isLoading && <LoadingBlock />}
      {error && <ErrorBlock />}

      {data && (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <input
                    type="checkbox"
                    aria-label="전체 선택"
                    className="size-4 accent-[var(--color-primary)]"
                    checked={rows.length > 0 && rows.every((lead) => lead.leadId in selected)}
                    onChange={(e) => toggleAll(e.target.checked)}
                  />
                </TableHead>
                <TableHead>리드 ID</TableHead>
                <TableHead>이름</TableHead>
                <TableHead>연락처</TableHead>
                <TableHead>단계</TableHead>
                <TableHead>우선순위</TableHead>
                <TableHead>참석</TableHead>
                <TableHead>담당자</TableHead>
                <TableHead>체류일</TableHead>
                <TableHead>전환일</TableHead>
                <TableHead className="text-right">재신청</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <EmptyRow colSpan={11}>
                  <Users className="mx-auto mb-2 size-8 opacity-40" />
                  조건에 맞는 리드가 없습니다
                </EmptyRow>
              )}
              {rows.map((lead) => (
                <TableRow
                  key={lead.leadId}
                  className="cursor-pointer"
                  onClick={() => navigate(`/lc-crm/leads/${lead.leadId}`)}
                >
                  {/* 행 전체가 상세 진입이므로, 행 안의 다른 인터랙션은 전파를 끊는다. */}
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={`리드 ${lead.leadId} 선택`}
                      className="size-4 accent-[var(--color-primary)]"
                      checked={lead.leadId in selected}
                      onChange={(e) => toggle(lead, e.target.checked)}
                    />
                  </TableCell>
                  <TableCell className="tabular-nums">{lead.leadId}</TableCell>
                  <TableCell>
                    {/* 이름 링크는 키보드 접근과 새 탭 열기를 위해 행 클릭과 병행한다. */}
                    <Link
                      className="font-medium text-primary underline-offset-4 hover:underline"
                      to={`/lc-crm/leads/${lead.leadId}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {lead.contactName ?? `#${lead.leadId}`}
                    </Link>
                  </TableCell>
                  <TableCell className="tabular-nums">{formatPhone(lead.phoneNormalized)}</TableCell>
                  <TableCell><StageBadge stage={lead.stage} /></TableCell>
                  <TableCell><PriorityBadge priority={lead.priority} /></TableCell>
                  <TableCell>{labelOf(ATTENDANCE_STATUS_LABEL, lead.attendanceStatus)}</TableCell>
                  <TableCell>{lead.assignedTeam ?? '-'}</TableCell>
                  <TableCell>
                    <StaleDaysCell
                      lastActivityAt={lead.lastActivityAt}
                      stageEnteredAt={lead.stageEnteredAt}
                    />
                  </TableCell>
                  <TableCell className="tabular-nums">{formatKstDate(lead.convertedAt)}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {lead.reapplicationCount > 0 ? (
                      <Badge variant="secondary">{lead.reapplicationCount}회</Badge>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Pagination
            page={filter.page ?? 0}
            totalPages={data.totalPages}
            totalElements={data.totalElements}
            onChange={(page) => {
              setFilter((prev) => ({ ...prev, page }))
              setSelected({})
            }}
          />
        </>
      )}

      {bulkOpen && (
        <BulkStageDialog
          leads={(data?.content ?? []).filter((lead) => lead.leadId in selected)}
          onClose={() => setBulkOpen(false)}
          onDone={() => {
            setBulkOpen(false)
            setSelected({})
            queryClient.invalidateQueries({ queryKey: ['lc-crm-leads'] })
          }}
        />
      )}
    </div>
  )
}

/**
 * FR-23 일괄 단계 변경.
 *
 * 리드마다 목록에서 읽은 버전을 그대로 싣는다. 한 건이라도 낡았으면 서버가 batch 전체를
 * 되돌린다 — 부분 성공은 무엇이 반영됐는지 알 수 없게 만든다.
 */
function BulkStageDialog({
  leads,
  onClose,
  onDone,
}: {
  leads: LeadListItem[]
  onClose: () => void
  onDone: () => void
}) {
  const [toStage, setToStage] = useState<string>('CONSULTING')
  const [reasonCode, setReasonCode] = useState('')
  const [reasonNote, setReasonNote] = useState('')
  const mutation = useMutation({
    mutationFn: async () => {
      // 목록 DTO에는 version이 없다. 낙관적 잠금을 지키려면 각 리드의 현재 버전을 먼저 읽는다.
      const details = await Promise.all(leads.map((lead) => fetchLeadDetail(lead.leadId)))
      return bulkChangeStage({
        toStage,
        targets: details.map((detail) => ({
          leadId: detail.leadId,
          expectedVersion: detail.version,
        })),
        reasonCode: reasonCode || undefined,
        reasonNote: reasonNote || undefined,
      })
    },
    onSuccess: (result) => {
      toast.success(`${result.changedCount}건을 ${labelOf(LEAD_STAGE_LABEL, result.toStage)}로 옮겼습니다`)
      onDone()
    },
    onError: (err) => toast.error(handleApiError(err) || '일괄 단계 변경 실패'),
  })

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>선택 리드 단계 변경</DialogTitle>
          <DialogDescription>
            {leads.length}건을 같은 단계로 옮깁니다. 한 건이라도 막히면 전체가 취소됩니다.
          </DialogDescription>
        </DialogHeader>

        <FieldGroup>
          <Field>
            <FieldLabel>옮길 단계</FieldLabel>
            <LabeledSelect
              ariaLabel="옮길 단계"
              value={toStage}
              onChange={setToStage}
              items={LEAD_STAGE_ORDER.map((stage) => ({ value: stage, label: LEAD_STAGE_LABEL[stage] }))}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="bulk-reason-code">사유 코드 (선택)</FieldLabel>
            <Input
              id="bulk-reason-code"
              value={reasonCode}
              onChange={(e) => setReasonCode(e.target.value)}
              placeholder="예: SCHEDULED"
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="bulk-reason-note">메모 (선택)</FieldLabel>
            <Textarea
              id="bulk-reason-note"
              rows={2}
              value={reasonNote}
              onChange={(e) => setReasonNote(e.target.value)}
            />
          </Field>
        </FieldGroup>

        <div className="max-h-40 overflow-y-auto rounded-md border border-border p-2 text-sm">
          {leads.map((lead) => (
            <div key={lead.leadId} className="flex justify-between py-0.5">
              <span>
                {lead.contactName ?? '-'} <span className="text-muted-foreground tabular-nums">#{lead.leadId}</span>
              </span>
              <span className="text-muted-foreground">{labelOf(LEAD_STAGE_LABEL, lead.stage)}</span>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
            {leads.length}건 변경
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
